import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    LinkIcon,
    PaperAirplaneIcon,
    UserPlusIcon
} from '@heroicons/react/24/outline';
import { CreateDmPatientLanding } from '../../patient-files/actions/dm/CreateDmPatientLanding';
import { LinkExistingDmPatientLanding } from '../../patient-files/actions/dm/LinkExistingDmPatientLanding';
import { SendActivationLinkLanding } from '../../patient-files/actions/dm/SendActivationLinkLanding';
import { advanceWorkflow } from '../../../services/workQueue';
import { usePatientCard } from '../../../hooks/usePatientCard';
import { useToast } from '../../../providers/ToastProvider';
import type { TaskBodyProps } from './index';
import { findOdoForwardAction } from '../odoForwardAction';

type StepId = 'patient' | 'activation';
type Mode = 'create' | 'link';

interface StepMeta {
    id: StepId;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
}

const STEPS: StepMeta[] = [
    { id: 'patient', label: 'DM patient', icon: UserPlusIcon },
    { id: 'activation', label: 'Activation link', icon: PaperAirplaneIcon }
];

const noop = () => {
    /* TaskSurface / the wizard footer own navigation */
};

/**
 * Step 11 — "Setup DM". A two-step wizard that reuses the existing DM action
 * landings (create/link a DM patient, then send the activation link). Owns its
 * own submit: the workflow advances to "Pending patient record update" only
 * after the activation link has been sent.
 */
export function SetupDmTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const { showToast } = useToast();
    const { data: card, refetch: refetchCard } = usePatientCard(c.id);

    const dmId = card?.monitoring?.dmId ?? null;
    const isLinked = dmId != null;
    const activationCount = card?.monitoring?.dmLinkActivationCount ?? 0;

    const [stepIndex, setStepIndex] = useState(0);
    const [mode, setMode] = useState<Mode>('create');
    const [activationSent, setActivationSent] = useState(false);
    const [advancing, setAdvancing] = useState(false);

    const forwardAction = findOdoForwardAction(statusMeta.outgoingActions);

    // This task drives its own completion — hide TaskSurface's action buttons.
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: false, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const currentStep = STEPS[stepIndex];
    const hasSentActivation = activationSent || activationCount > 0;

    const canAdvanceStep = useMemo(() => {
        if (currentStep.id === 'patient') return isLinked;
        return hasSentActivation;
    }, [currentStep.id, isLinked, hasSentActivation]);

    const goNext = useCallback(() => {
        setStepIndex((i) => Math.min(i + 1, STEPS.length - 1));
    }, []);

    const goBack = useCallback(() => {
        setStepIndex((i) => Math.max(i - 1, 0));
    }, []);

    const handleFinish = useCallback(async () => {
        setAdvancing(true);
        try {
            await advanceWorkflow(c.id, 'create-patient-in-dm', {});
            onTaskComplete?.(forwardAction);
        } catch {
            showToast('Failed to advance case after DM setup. Please try again.', 'error');
        } finally {
            setAdvancing(false);
        }
    }, [c.id, forwardAction, onTaskComplete, showToast]);

    return (
        <div className="flex-1 min-h-0 flex flex-col">
            {/*  Stepper  */}
            <div className="flex items-center justify-center gap-2 px-5 py-4 border-b border-gray-200 flex-shrink-0">
                {STEPS.map((step, index) => {
                    const Icon = step.icon;
                    const isActive = index === stepIndex;
                    const isPast = index < stepIndex;
                    // Only allow jumping back, or forward once the current step is satisfied.
                    const reachable =
                        index <= stepIndex || (index === stepIndex + 1 && canAdvanceStep);
                    return (
                        <div key={step.id} className="flex items-center">
                            <button
                                type="button"
                                disabled={!reachable}
                                onClick={() => reachable && setStepIndex(index)}
                                aria-current={isActive ? 'step' : undefined}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 ${
                                    isActive
                                        ? 'bg-gray-900 text-white cursor-pointer'
                                        : isPast
                                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200 cursor-pointer'
                                          : reachable
                                            ? 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-700 cursor-pointer'
                                            : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                }`}
                            >
                                <Icon className="w-3.5 h-3.5" />
                                <span>{step.label}</span>
                            </button>
                            {index < STEPS.length - 1 && (
                                <div
                                    className={`w-6 h-px mx-1 ${isPast ? 'bg-gray-300' : 'bg-gray-200'}`}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {/*  Body  */}
            <div className="flex-1 min-h-0 overflow-auto p-6">
                {currentStep.id === 'patient' ? (
                    <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
                        {isLinked ? (
                            <div className="bg-white rounded-xl border border-gray-200 p-6">
                                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                                    Patient linked to Dental Monitoring
                                </p>
                                <p className="text-sm text-gray-700">
                                    This patient is linked to DM patient{' '}
                                    <span className="font-mono text-gray-900">#{dmId}</span>.
                                    Continue to send the activation link.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/*  Create / Link toggle  */}
                                <div className="inline-flex self-start rounded-lg border border-gray-200 bg-gray-50 p-0.5">
                                    <button
                                        type="button"
                                        onClick={() => setMode('create')}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                            mode === 'create'
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        <UserPlusIcon className="w-4 h-4" />
                                        Create new
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setMode('link')}
                                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                            mode === 'link'
                                                ? 'bg-white text-gray-900 shadow-sm'
                                                : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        <LinkIcon className="w-4 h-4" />
                                        Link existing
                                    </button>
                                </div>

                                {mode === 'create' ? (
                                    <CreateDmPatientLanding
                                        patientId={c.id}
                                        patientName={c.name}
                                        onBack={noop}
                                        inWorkflow
                                        onCompleted={() => {
                                            void refetchCard();
                                        }}
                                    />
                                ) : (
                                    <LinkExistingDmPatientLanding
                                        patientId={c.id}
                                        patientName={c.name}
                                        onBack={noop}
                                        inWorkflow
                                        onCompleted={() => {
                                            void refetchCard();
                                        }}
                                    />
                                )}
                            </>
                        )}
                    </div>
                ) : (
                    <SendActivationLinkLanding
                        patientId={c.id}
                        patientName={c.name}
                        onBack={noop}
                        inWorkflow
                        onSent={() => setActivationSent(true)}
                    />
                )}
            </div>

            {/*  Footer  */}
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 flex-shrink-0">
                <button
                    type="button"
                    onClick={goBack}
                    disabled={stepIndex === 0 || advancing}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    <ChevronLeftIcon className="w-4 h-4" />
                    Back
                </button>

                {currentStep.id === 'activation' ? (
                    <button
                        type="button"
                        onClick={handleFinish}
                        disabled={!hasSentActivation || advancing}
                        className="flex items-center gap-2 bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {advancing ? 'Finishing…' : 'Finish & advance'}
                    </button>
                ) : (
                    <button
                        type="button"
                        onClick={goNext}
                        disabled={!canAdvanceStep}
                        className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        Next
                        <ChevronRightIcon className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
